import { BillableElement } from "../../../../reducers/uploadedBoard";
import { displayProperty } from "../../../../utils/properties";
import * as styledElements from "./SelectPartStyles";
import React, { ReactNode } from "react";
import { InputLabel } from "../../../../constants/Styles";
import usePart from "../../../../utils/hooks/commonly-stored/parts";
import { ProcessingPill } from "../../ProjectDetails";
import Button from "../../../common/Button";
import useFootprintForPart from "../../../../utils/hooks/footprints/forPart";
import { useAppSelector } from "../../../../utils/hooks/store";
import { ADMIN_GROUP, ENGINEER_GROUP } from "../../../../utils/group";
import useRemoveFootprintPartAssociation from "../../../../utils/hooks/footprints/removeAssociation";

function ElementProperty({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) {
  return (
    <styledElements.PropertyContainer>
      <InputLabel>{label}:</InputLabel>
      {value || "-"}
    </styledElements.PropertyContainer>
  );
}

function FootprintLine({
  element,
  setAddingFootprint,
}: {
  element: BillableElement;
  setAddingFootprint?: (adding: boolean) => void;
}) {
  const { groups } = useAppSelector((state) => state.auth);
  const canEditFootprint =
    groups.includes(ADMIN_GROUP) || groups.includes(ENGINEER_GROUP);
  const { part } = usePart(element.part_id);
  const { footprint, loading: loadingFootprint } = useFootprintForPart(
    part?.id || null,
  );
  const { removeFootprintPartAssociation } = useRemoveFootprintPartAssociation(
    element.part_id,
  );

  if (element.part_id === null) {
    return null;
  }

  return (
    <>
      <styledElements.FootprintsContainer>
        <ElementProperty
          label={"Current footprint"}
          value={footprint?.name || "None"}
        />
        {canEditFootprint &&
          footprint === null &&
          !loadingFootprint &&
          setAddingFootprint !== undefined && (
            <Button
              label={"Add footprint for part"}
              isSmallSize
              onClick={() => setAddingFootprint(true)}
            />
          )}
        {canEditFootprint && footprint !== null && !loadingFootprint && (
          <Button
            label={"Remove footprint for part"}
            isSmallSize
            onClick={() =>
              removeFootprintPartAssociation(element.part_id, footprint.id)
            }
          />
        )}
      </styledElements.FootprintsContainer>
    </>
  );
}

export function SelectPartProperties({
  element,
  setAddingFootprint,
}: {
  element: BillableElement;
  setAddingFootprint?: (adding: boolean) => void;
}) {
  const { groups } = useAppSelector((state) => state.auth);
  const showParsedProperties =
    groups.includes(ADMIN_GROUP) || groups.includes(ENGINEER_GROUP);

  return (
    <ElementProperties
      element={element}
      setAddingFootprint={setAddingFootprint}
    >
      {element.part_specification?.mpn && (
        <ElementProperty
          label={"MPN specified"}
          value={element.part_specification.mpn}
        />
      )}
      {element.part_specification?.manufacturer && (
        <ElementProperty
          label={"Manufacturer specified"}
          value={element.part_specification.manufacturer}
        />
      )}
      {element.footprint_name && (
        <ElementProperty
          label={"Footprint specified"}
          value={element.footprint_name}
        />
      )}
      {showParsedProperties && element.properties.length > 0 && (
        <styledElements.AdminPanel>
          <styledElements.AdminTitle>Admin only:</styledElements.AdminTitle>
          <styledElements.PropertiesContainer>
            {element.properties.map((property, propertyIndex) => {
              return (
                <ElementProperty
                  key={propertyIndex.toString() + JSON.stringify(property)}
                  label={property.parameter.name}
                  value={displayProperty(property)}
                />
              );
            })}
          </styledElements.PropertiesContainer>
        </styledElements.AdminPanel>
      )}
    </ElementProperties>
  );
}

export function UpdatePlacementProperties({
  element,
  setAddingFootprint,
}: {
  element: BillableElement;
  setAddingFootprint?: (adding: boolean) => void;
}) {
  return (
    <ElementProperties
      element={element}
      setAddingFootprint={setAddingFootprint}
    />
  );
}

function ElementProperties({
  element,
  children,
  setAddingFootprint,
}: {
  element: BillableElement;
  children?: ReactNode;
  setAddingFootprint?: (adding: boolean) => void;
}) {
  const { part } = usePart(element.part_id);
  return (
    <styledElements.PropertiesContainer>
      <styledElements.Title>{element.name}</styledElements.Title>
      {element.part_id !== null && (
        <ElementProperty
          label={"Current part"}
          value={part?.name || <ProcessingPill />}
        />
      )}
      <ElementProperty label={"Value text"} value={element.value_text} />
      {children}
      <FootprintLine
        element={element}
        setAddingFootprint={setAddingFootprint}
      />
    </styledElements.PropertiesContainer>
  );
}
